import { action, makeObservable, observable } from "mobx";
import {
  getDocumentByUuid,
  listDocuments,
  createDocument,
  attachDocument,
  getDocumentAgents,
  unassignAgentFromDocument,
  deleteDocument as deleteDocumentService
} from "../../services";

class DocumentStore {
  document = {
    name: '',
    description: '',
    uuid: '',
    state: ''
  };
  documentList = {
    data: [],
    meta: {
      total_items: 0,
      total_pages: 0,
      current_page: 1,
      per_page: 500,
      has_next: false,
      has_previous: false,
      next_cursor: ''
    },
    msg_code: ''
  };
  documentCreated = {
    uuid: '',
    name: '',
    msgCode: '',
    status: '',
    updatedAt: ''
  };
  documentAttached = {
    document_uuid: '',
    file_uuid: '',
    msg_code: '',
    status: '',
    updated_at: ''
  };
  agentAssigned = {
    document_uuid: '',
    agent_uuid: '',
    privilege: '',
    expires_at: '',
    msg_code: '',
    updated_at: ''
  };
  documentAgents = {
    data: [],
    msg_code: '',
    request_id: ''
  };
  agentUnassigned = {
    data: {
      document_uuid: '',
      agent_uuid: '',
      unassigned_at: ''
    },
    msg_code: '',
    request_id: ''
  };
  isLoadingDocuments = false;
  isErrorLoadingDocuments = false;
  isDocumentsErrorMessage = '';
  isDeletingDocument = false;
  deleteError = null;

  constructor() {
    makeObservable(this, {
      document: observable,
      documentList: observable,
      documentCreated: observable,
      documentAttached: observable,
      agentAssigned: observable,
      documentAgents: observable,
      agentUnassigned: observable,
      isLoadingDocuments: observable,
      isErrorLoadingDocuments: observable,
      isDocumentsErrorMessage: observable,
      isDeletingDocument: observable,
      deleteError: observable,
      setDocument: action,
      setDocumentList: action,
      setDocumentCreated: action,
      setDocumentAttached: action,
      setAgentAssigned: action,
      setDocumentAgents: action,
      setAgentUnassigned: action,
      fetchDocument: action,
      fetchDocumentList: action,
      createDocument: action,
      attachDocument: action,
      fetchDocumentAgents: action,
      unassignAgent: action,
      setIsDocumentsErrorMessage: action,
      resetState: action,
      pushDocument: action,
      updateDocumentStateByName: action,
      deleteDocument: action,
      deleteMultipleDocuments: action,
      removeDocumentFromList: action,
      removeMultipleDocumentsFromList: action,
    });
  }

  setDocument = (data) => { this.document = data; };
  setDocumentList = (data) => { this.documentList = data; };
  setDocumentCreated = (data) => { this.documentCreated = data; };
  setDocumentAttached = (data) => { this.documentAttached = data; };
  setAgentAssigned = (data) => { this.agentAssigned = data; };
  setDocumentAgents = (data) => { this.documentAgents = data; };
  setAgentUnassigned = (data) => { this.agentUnassigned = data; };
  setIsDocumentsErrorMessage = (error) => { this.isDocumentsErrorMessage = error; };

  
  resetState = () => {
    this.isLoadingDocuments = false;
    this.isErrorLoadingDocuments = false;
    this.isDocumentsErrorMessage = '';
    this.documentList = {
      data: [],
      meta: {
        total_items: 0,
        total_pages: 0,
        current_page: 1,
        per_page: 500,
        has_next: false,
        has_previous: false,
        next_cursor: ''
      },
      msg_code: ''
    };
  };

 
  hasLoadedData = () => {
    return this.documentList.data && this.documentList.data.length > 0;
  };

 
  isInLoadingState = () => {
    return this.isLoadingDocuments || this.isErrorLoadingDocuments;
  };

  fetchDocument = async (uuid) => {
    const { data } = await getDocumentByUuid(uuid);
    this.setDocument(data);
  };
  
  updateDocumentStateByName = (name, newState) => {
    const docIndex = this.documentList.data.findIndex(doc => doc.name === name);
    if (docIndex !== -1) {
      //  Create new array to trigger MobX reactivity
      this.documentList = {
        ...this.documentList,
        data: this.documentList.data.map((doc, idx) => 
          idx === docIndex ? { ...doc, state: newState } : doc
        )
      };
      console.log('updateDocumentStateByName: Updated', name, 'to state:', newState);
    } else {
      console.warn('updateDocumentStateByName: Document not found:', name);
    }
  }

  fetchDocumentList = async (body) => {    
    try {
      this.isLoadingDocuments = true;
      this.isErrorLoadingDocuments = false;
      this.isDocumentsErrorMessage = '';
      const { data } = await listDocuments(body);
      this.setDocumentList(data);
    } catch (error) {
      this.isErrorLoadingDocuments = true;
      this.setIsDocumentsErrorMessage("Error fetching documents list, try again later");
    } finally {
      this.isLoadingDocuments = false;
    }
  };

  pushDocument = ({ name, state = 'loading' }) => {
    //  Add document to beginning of list with loading state
    const newDocument = { 
      name,  
      uuid: `temp-${Date.now()}`, 
      state: state,
      type: 'document',
      metadata: {},
      created_at: new Date().toISOString()
    };
    
    //  Create new array to trigger MobX reactivity
    this.documentList = {
      ...this.documentList,
      data: [newDocument, ...(this.documentList.data || [])]
    };
    
    console.log('pushDocument: Added document with state:', state, '| Total docs:', this.documentList.data.length);
  }

  removeDocumentFromList = (documentUuid) => {
    //  Remove document from list without API call
    this.documentList = {
      ...this.documentList,
      data: this.documentList.data.filter(doc => doc.uuid !== documentUuid),
      meta: {
        ...this.documentList.meta,
        total_items: this.documentList.meta.total_items - 1
      }
    };
    console.log('removeDocumentFromList: Removed document', documentUuid, '| Remaining docs:', this.documentList.data.length);
  }

  removeMultipleDocumentsFromList = (documentUuids) => {
    //  Remove multiple documents from list without API call
    const uuidsSet = new Set(documentUuids);
    this.documentList = {
      ...this.documentList,
      data: this.documentList.data.filter(doc => !uuidsSet.has(doc.uuid)),
      meta: {
        ...this.documentList.meta,
        total_items: this.documentList.meta.total_items - documentUuids.length
      }
    };
    console.log('removeMultipleDocumentsFromList: Removed', documentUuids.length, 'documents | Remaining docs:', this.documentList.data.length);
  }
  
  createDocument = async (body) => {
    const { data } = await createDocument(body);
    this.setDocumentCreated(data);
  };

  attachDocument = async (body) => {
    const { data } = await attachDocument(body);
    this.setDocumentAttached(data);
  };

  fetchDocumentAgents = async (uuid, tenant_id) => {
    const { data } = await getDocumentAgents(uuid, tenant_id);
    this.setDocumentAgents(data);
  };

  unassignAgent = async (uuid, agent_id, tenant_id) => {
    const { data } = await unassignAgentFromDocument(uuid, agent_id, tenant_id);
    this.setAgentUnassigned(data);
  };

  /**
   * Delete a single document from agent (unassign + delete)
   * @param {string} documentUuid - Document UUID
   * @param {string} agentId - Agent ID
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  deleteDocument = async (documentUuid, agentId, tenantId) => {
    this.isDeletingDocument = true;
    this.deleteError = null;

    try {
      // Step 1: Unassign document from agent
      console.log(`Unassigning document ${documentUuid} from agent ${agentId}`);
      await unassignAgentFromDocument(documentUuid, agentId);
      console.log('Document unassigned successfully');

      // Step 2: Delete the document using the dedicated endpoint
      console.log(`Deleting document ${documentUuid}`);
      const deleteBody = {
        document_uuid: documentUuid,
      };
      await deleteDocumentService(deleteBody);
      console.log('Document deleted successfully');

      return { success: true };
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error deleting document';
      this.deleteError = errorMessage;
      return { success: false, error: errorMessage };
    } finally {
      this.isDeletingDocument = false;
    }
  };

  /**
   * Delete multiple documents from agent (batch operation)
   * @param {Array<string>} documentUuids - Array of document UUIDs
   * @param {string} agentId - Agent ID
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<{successCount: number, failedCount: number, errors: Array}>}
   */
  deleteMultipleDocuments = async (documentUuids, agentId, tenantId) => {
    this.isDeletingDocument = true;
    this.deleteError = null;

    let successCount = 0;
    let failedCount = 0;
    const errors = [];

    try {
      const processDocumentDeletion = async (documentUuid) => {
        try {
          await unassignAgentFromDocument(documentUuid, agentId, tenantId);

          const deleteBody = {
            document_uuid: documentUuid,
          };
          await deleteDocumentService(deleteBody);

          return { success: true, documentUuid };
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
          return { 
            success: false, 
            documentUuid, 
            error: errorMessage 
          };
        }
      };

      const results = await Promise.allSettled(
        documentUuids.map(documentUuid => processDocumentDeletion(documentUuid))
      );

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const deletionResult = result.value;
          if (deletionResult.success) {
            successCount++;
          } else {
            failedCount++;
            errors.push({
              documentUuid: deletionResult.documentUuid,
              error: deletionResult.error
            });
          }
        } else {
          failedCount++;
          errors.push({
            documentUuid: documentUuids[index],
            error: result.reason?.message || 'Unknown error'
          });
        }
      });

      return { successCount, failedCount, errors };
    } catch (error) {
      this.deleteError = error.message || 'Error deleting documents';
      return { successCount, failedCount, errors: [{ error: error.message }] };
    } finally {
      this.isDeletingDocument = false;
    }
  };
}

export default DocumentStore;
